import { useEffect } from 'react'

import useUser from '../../services/useUser'
import { getPushAdapter } from '../../services/platform/adapters/push'

export default function EmployerMessagingRegistration() {
  const user = useUser()
  const claimsLoading = user?.claimsLoading

  useEffect(() => {
    // Register push for every signed-in user (employees + employers). On
    // web, the actual permission prompt only fires once a VAPID key is
    // configured; without it register() short-circuits harmlessly. On
    // native (#1695), getPushAdapter() routes this to
    // registerNativePushNotifications() instead — before this ticket, the
    // native branch was an explicit UNAVAILABLE stub, so this mount used to
    // throw on every native launch (caught below, but silently: native
    // registration never actually ran).
    if (!user?.uid || claimsLoading) {
      return undefined
    }

    const { register, initForegroundDisplay } = getPushAdapter()
    let cancelled = false
    let stopForeground = () => {}

    const run = async () => {
      try {
        const result = await register()
        // This used to test for a 'failed' status the service NEVER returns, so
        // every real failure — unsupported browser, denied permission, a bad
        // VAPID key — was completely silent. Push was dead for every user in
        // production and no signal existed anywhere. Report what actually came
        // back instead.
        if (!cancelled && result?.status !== 'registered') {
          console.warn('Push notifications not active:', result?.status)
        }

        // A notification that arrives while the app is open never reaches the
        // service worker — without this it is dropped in silence. (Native's
        // own initForegroundDisplay is a documented no-op — see push.js —
        // since the OS itself displays a foreground native push.)
        const unsubscribe = await initForegroundDisplay()
        if (cancelled) {
          unsubscribe()
        } else {
          stopForeground = unsubscribe
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to register push notifications:', error)
        }
      }
    }

    run()

    return () => {
      cancelled = true
      stopForeground()
    }
  }, [claimsLoading, user?.uid])

  return null
}